import React, { FC, useState, Fragment, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router';

import { Match, Course, Matches } from '../../../../database.schema';
import { ResultEntryPairs } from './result-entry-pairs';
import { ResultEntrySingles } from './result-entry-singles';
import { UpdateButton } from './update-button';
import { useFetchCourses } from '../../../api/hooks/use-fetch-courses';
import { FlexRowStart } from '../../common-styles/common-styles';
import { MatchEntryInput, MatchEntryLabel } from '../match-entry-styles';
import { MatchDayResultScoreCard, useFetchMatchDayResultScoreCard } from '../../../api/hooks/use-fetch-match-day-results-with-scorecard';

export const TeamWrapper = styled.div<{ bgColor?: string | null }>`
  border: 1px solid black;
  border-radius: 0.25rem;
  width: 8.25rem;
  background: ${(props) => props.bgColor || '#eae6e6'};
  color: white;
  padding: 5px;
  min-height: 2rem;
  margin: 0.25rem 0;
  text-align: center;
`;
const CourseDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0.5rem 0;
  select {
    height: 1.5rem;
    margin-left: 0.25rem;
  }
  label {
    margin-right: 0.5rem;
  }
`;
const FlexEven = styled.div`
  display: flex;
  justify-content: space-evenly;
`;

const Button = styled.button`
  border-radius: 5px;
  width: 4rem;
  height: 1.5rem;
  background: lightblue;
  border: 1px solid #5c9db2;
  font-weight: 500;
  color: white;
  margin: 1rem 0;
  &:disabled {
    background-color: lightgray;
  }
`;

type ResultEditEntryProps = {
  recordedMatches: Matches;
  course: Course;
  setShowEdit: React.Dispatch<React.SetStateAction<boolean>>;
  resultIndex: number;
};
//{ recordedMatches, course, setShowEdit, resultIndex
export const ResultEditEntry: FC = (): JSX.Element | null => {
  const navigate = useNavigate();
  const { data: coursesList } = useFetchCourses();
  const courses = coursesList as Array<Course>;
  let params = useParams();
  const resultIndex = Number(params.dayIndex);
  const selectedTournamentIndex = Number(params.tournamentIndex);
  const { isLoading, error, data } = useFetchMatchDayResultScoreCard(selectedTournamentIndex, resultIndex);

  const [recordedMatches, setRecordedMatches] = useState<Matches>();
  const [resultHolder, setResultHolder] = useState<Match[]>([]);
  const [updateStatus, setUpdateStatus] = useState({ success: false, error: false });
  const [startingHole, setStartingHole] = useState<Number>(1);
  const [resultsValid, setResultsValid] = useState(false);
  useEffect(() => {
    if (data) {
      const { course, results, format, startingHole: initialStartingHole } = data as MatchDayResultScoreCard;
      setRecordedMatches({ format: format as 'Pairs' | 'Singles', courseId: course.id, matches: results, startingHole: initialStartingHole });
      setResultHolder(results);
      setStartingHole(initialStartingHole ?? 1);
    }
  }, [data]);

  const onChangePlayerScore = (points: number | string, playerIndex: number, matchIndex: number, team: string) => {
    if (Number(points)) {
      if (team === 'team1') {
        const recordedMatchesClone = [...resultHolder];
        const team1PlayersClone = [...recordedMatchesClone[matchIndex].team1.players];
        team1PlayersClone.splice(playerIndex, 1, { ...team1PlayersClone[playerIndex], points: Number(points) });
        const newMatch: Match = { ...recordedMatchesClone[matchIndex], team1: { ...recordedMatchesClone[matchIndex].team1, players: team1PlayersClone } };
        recordedMatchesClone.splice(matchIndex, 1, newMatch);
        setResultHolder([...recordedMatchesClone]);
      }
      if (team === 'team2') {
        const recordedMatchesClone = [...resultHolder];
        const team2PlayersClone = [...recordedMatchesClone[matchIndex].team2.players];
        team2PlayersClone.splice(playerIndex, 1, { ...team2PlayersClone[playerIndex], points: Number(points) });
        const newMatch: Match = { ...recordedMatchesClone[matchIndex], team2: { ...recordedMatchesClone[matchIndex].team2, players: team2PlayersClone } };
        recordedMatchesClone.splice(matchIndex, 1, newMatch);
        setResultHolder([...recordedMatchesClone]);
      }
    }
  };

  const onMatchResultChange = (result: string, matchIndex: number) => {
    const recordedMatchClone = [...resultHolder];
    const matchClone = { ...recordedMatchClone[matchIndex] };
    if (result === 'beat') {
      const team1Clone = { ...recordedMatchClone[matchIndex].team1, points: 1 as 1 | 0 | 0.5 };
      const team2Clone = { ...recordedMatchClone[matchIndex].team2, points: 0 as 1 | 0 | 0.5 };
      recordedMatchClone.splice(matchIndex, 1, { ...matchClone, team1: { ...team1Clone }, team2: { ...team2Clone } });
    }
    if (result === 'lost to') {
      const team1Clone = { ...recordedMatchClone[matchIndex].team1, points: 0 as 1 | 0 | 0.5 };
      const team2Clone = { ...recordedMatchClone[matchIndex].team2, points: 1 as 1 | 0 | 0.5 };
      recordedMatchClone.splice(matchIndex, 1, { ...matchClone, team1: { ...team1Clone }, team2: { ...team2Clone } });
    }
    if (result === 'halved') {
      const team1Clone = { ...recordedMatchClone[matchIndex].team1, points: 0.5 as 1 | 0 | 0.5 };
      const team2Clone = { ...recordedMatchClone[matchIndex].team2, points: 0.5 as 1 | 0 | 0.5 };
      recordedMatchClone.splice(matchIndex, 1, { ...matchClone, team1: { ...team1Clone }, team2: { ...team2Clone } });
    }
    setResultHolder(recordedMatchClone);
  };

  const onMatchMarginChange = (resultMargin: string, matchIndex: number) => {
    const recordedMatchClone = [...resultHolder];
    const matchClone = { ...recordedMatchClone[matchIndex], resultMargin };
    recordedMatchClone.splice(matchIndex, 1, matchClone);
    setResultHolder(recordedMatchClone);
  };

  const isNotValid = (): void => {
    let valid = true;
    const form = document.querySelector('form')!;
    if (form) {
      const formData = new FormData(form);
      formData.forEach((value, key) => {
        if (value === '') {
          if (key.startsWith('team-result-margin-')) {
            const index = key.substring(key.length - 1);
            if (formData.get(`team-result-${index}`) === 'beat' || formData.get(`team-result-${index}`) === 'lost to') {
              valid = false;
            }
          } else {
            valid = false;
          }
          return;
        }
      });
    }
    setResultsValid(valid);
  };

  if (isLoading) {
    return <div>loading</div>;
  }
  if (error) {
    console.log('><><', error);
    return <div>error</div>;
  }
  if (!recordedMatches) {
    return <div>loading</div>;
  }

  return (
    <Fragment>
      {updateStatus.error && <div>Update failed!!!!</div>}
      <form id="form" name="results" onSubmit={(event) => event.preventDefault()} onChange={() => isNotValid()} autoComplete="off">
        <h2>Editing Day {resultIndex + 1}</h2>
        <FlexRowStart>
          <CourseDisplay>
            <div>
              <strong>Course:</strong>
              <select name="course" defaultValue={recordedMatches?.courseId || -1}>
                <option value="">Course...</option>
                {courses.map((aCourse: Course) => (
                  <option key={aCourse.name} value={`${aCourse.id}`}>
                    {aCourse.name}
                  </option>
                ))}
              </select>
            </div>
          </CourseDisplay>
        </FlexRowStart>
        <div>
          <FlexRowStart>
            <strong>Starting Hole:</strong>
            <MatchEntryInput
              type="radio"
              name="startingHole"
              value="1"
              id="1"
              data-testid="first"
              checked={startingHole === 1}
              onChange={() => {
                setStartingHole(1);
              }}
            />
            <MatchEntryLabel htmlFor="pairs">First</MatchEntryLabel>
            <MatchEntryInput
              type="radio"
              name="startingHole"
              value="10"
              id="10"
              data-testid="tenth"
              checked={startingHole === 10}
              onChange={() => {
                setStartingHole(10);
              }}
            />

            <MatchEntryLabel htmlFor="singles">Tenth</MatchEntryLabel>
          </FlexRowStart>
        </div>
        <FlexEven>
          {recordedMatches?.format.toLowerCase() === 'pairs' ? (
            <ResultEntryPairs
              recordedMatch={resultHolder}
              onChangePlayerScore={onChangePlayerScore}
              onMatchResultChange={onMatchResultChange}
              onMatchMarginChange={onMatchMarginChange}
            />
          ) : (
            <ResultEntrySingles
              recordedMatch={resultHolder}
              onChangePlayerScore={onChangePlayerScore}
              onMatchMarginChange={onMatchMarginChange}
              onMatchResultChange={onMatchResultChange}
            />
          )}
        </FlexEven>
      </form>
      <FlexEven>
        <UpdateButton
          selectedTournamentIndex={selectedTournamentIndex}
          recordedMatches={{ ...recordedMatches, matches: [...resultHolder] }}
          resultIndex={resultIndex}
          onSuccess={() => navigate('/')}
          setUpdateStatus={setUpdateStatus}
          disabled={!resultsValid}
        />
        <Button type="button" onClick={() => navigate(`edit-results/${selectedTournamentIndex}/${resultIndex}`)}>
          Cancel
        </Button>
      </FlexEven>
    </Fragment>
  );
};
